import { Quaternion, Vector3 } from 'three'

import { LinkedItem } from '@types'

import { getRegletaEffectiveMm, isKARBCatalog } from '@utils/catalog/kbrCatalog'
import { getRinconeroRegletaFrontReachMm } from '@utils/sceneObjects/linked/freeDimensionLinkedCatalogs'
import { getMorphedItemWidth } from './projectItemOnSlabFaces'
import type { SnapGroupNode } from './snapGroups'

/**
 * Todo lo que hay que saber de un rinconero, en un único sitio.
 *
 * Hasta ahora cada consumidor repetía sus propias reglas y no coincidían: collideItems mira
 * `includes('i')` y otros `endsWith('i')`; el KARB se detecta con `includes('arb')` aquí y
 * con `includes('KARB')` allá; el saliente de la regleta se lee invertido en un sitio, en
 * crudo en otro y multiplicado por dos en collideItems. Esa dispersión es la razón de que
 * las caras de un rinconero salgan bien unas veces y mal otras.
 */

/** Patrones de catálogo de los rinconeros de for2home (los mismos que usa collideItems) */
const RINCONERO_CATALOG_PATTERNS = ['arp', 'alp', 'brp', 'blp', 'arb', 'alb', 'brb', 'blb']

/**
 * Holgura para dar por bueno el enganche en L. Los muebles miden 300mm de ancho para
 * arriba, así que sobra margen sin arriesgar falsos positivos, y absorbe el ajuste de
 * -50mm que el KARB aplica en unos sitios y en otros no.
 */
export const RINCONERO_LINK_TOLERANCE_MM = 90

const catalogOf = (node: SnapGroupNode): string => node.catalogID?.toString().toLowerCase() ?? ''

export function isRinconeroNode(node: SnapGroupNode): boolean {
  const catalog = catalogOf(node)
  return RINCONERO_CATALOG_PATTERNS.some((pattern) => catalog.includes(pattern))
}

/**
 * Rinconero izquierdo. Se mira el final del catálogo, no `includes('i')`: cualquier 'i'
 * suelta en la referencia daba izquierdo por error y con eso se invierte todo el enganche.
 */
export function isRinconeroIzquierdo(node: SnapGroupNode): boolean {
  return catalogOf(node).endsWith('i')
}

export function isKarbNode(node: SnapGroupNode): boolean {
  return isKARBCatalog(node.catalogID)
}

/**
 * Saliente REAL de la regleta en mm (valor sidebar/API; KBR/KARB no se invierten).
 */
export function getRinconeroRegletaMm(node: SnapGroupNode): number {
  return getRegletaEffectiveMm(node as LinkedItem)
}

const decompose = (node: SnapGroupNode) => {
  const position = new Vector3()
  const quaternion = new Quaternion()
  node.matrix.decompose(position, quaternion, new Vector3())
  return { position, quaternion }
}

/**
 * Dónde tiene que estar el ORIGEN del mueble que continúa la fila girando por el rinconero,
 * o `null` si `node` no es un rinconero con regleta.
 *
 * Es la misma cuenta que usa el dibujo de laterales (`findUnitSnappedToRegleta`), traída
 * aquí para que el registro de caras y el dibujo midan exactamente lo mismo:
 *
 * - rinconero derecho: el siguiente arranca en el extremo derecho del rinconero, empujado
 *   hacia delante por el fondo más el saliente de la regleta.
 * - rinconero izquierdo: el siguiente arranca girando 90°, retranqueado su propio ancho
 *   más el fondo y el saliente.
 *
 * El ancho del vecino entra en la cuenta del izquierdo, así que hay que probar candidato a
 * candidato en vez de calcular un único punto.
 */
export function getRinconeroLinkAnchor(
  node: SnapGroupNode,
  candidateWidth: number,
): Vector3 | null {
  if (!isRinconeroNode(node)) return null
  const reachMm = getRinconeroRegletaFrontReachMm(node as LinkedItem)
  const fallback = (node.thickness || 0) + getRinconeroRegletaMm(node)
  const depthMm = reachMm > 0 ? reachMm : fallback
  if (!(depthMm > 0)) return null

  const { position, quaternion } = decompose(node)

  if (!isRinconeroIzquierdo(node)) {
    const xAxis = new Vector3(1, 0, 0).applyQuaternion(quaternion)
    const zAxis = new Vector3(0, 0, 1).applyQuaternion(quaternion)
    return position
      .clone()
      .add(xAxis.multiplyScalar(getMorphedItemWidth(node)))
      .add(zAxis.multiplyScalar(depthMm))
  }

  const turned = quaternion
    .clone()
    .multiply(new Quaternion().setFromAxisAngle(new Vector3(0, 1, 0), Math.PI / 2))
  const xAxis = new Vector3(1, 0, 0).applyQuaternion(turned)
  return position.clone().add(xAxis.multiplyScalar(-(depthMm + candidateWidth)))
}

/**
 * Distancia entre dónde debería arrancar el mueble enganchado al giro del rinconero y
 * dónde arranca `candidate`, o `null` si no encaja. Sirve para registrar la cara del
 * rinconero sin depender de que el mueble haya pasado por el arrastre.
 */
export function getRinconeroLinkDistance(
  rinconero: SnapGroupNode,
  candidate: SnapGroupNode,
): number | null {
  const anchor = getRinconeroLinkAnchor(rinconero, getMorphedItemWidth(candidate))
  if (!anchor) return null
  const { position } = decompose(candidate)
  const distance = Math.hypot(anchor.x - position.x, anchor.z - position.z)
  return distance <= RINCONERO_LINK_TOLERANCE_MM ? distance : null
}
